import { MoreVertical } from 'lucide-react'
import { Spin } from 'antd'
import type { CellContext } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Robot } from '@/types/api'
import { formatDateTime } from '@/utils/format'
import { usePermission } from '@/hooks/use-permission'
import type { CombinedColumnDef, StyleConfig } from '@/api/dowload-excel.api'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  loadingRobotId?: string | null
  showUnitColumn?: boolean
  canView?: boolean
  canUpdate?: boolean
  onViewDetail?: (robot: Robot) => void
  onUpdate?: (robot: Robot) => void
  onSetupLocation?: (robot: Robot) => void
  onDelete?: (robot: Robot) => void
  onToggleStatus?: (robot: Robot) => void
}

interface RobotActionsCellProps {
  robot: Robot
  onViewDetail?: (robot: Robot) => void
  onUpdate?: (robot: Robot) => void
  onSetupLocation?: (robot: Robot) => void
  onDelete?: (robot: Robot) => void
}

export function getStatusBadgeClass(state: string) {
  const statusStyles: Record<string, string> = {
    'Đang hoạt động': 'bg-[#EAF7ED] text-[#2FB14F]',
    'Hoạt động': 'bg-[#EAF7ED] text-[#2FB14F]',
    'Không hoạt động': 'bg-[#E8EAEC] text-[#1E2F41]',
    'Đang không hoạt động': 'bg-[#E8EAEC] text-[#1E2F41]',
    'Đã xóa': 'bg-[#FDECEB] text-[#EE4036]',
  }
  return statusStyles[state] || 'bg-[#F1F5F9] text-[#475569]'
}

// Bảng cấu hình mã màu HEX chuẩn gửi cho Excel API
export const STATUS_EXCEL_STYLES: Record<string, StyleConfig | undefined> = {
  'Đang hoạt động': {
    backgroundColor: '#EAF7ED',
    textColor: '#2FB14F',
    bold: true,
  },
  'Hoạt động': { backgroundColor: '#EAF7ED', textColor: '#2FB14F', bold: true },
  'Không hoạt động': {
    backgroundColor: '#E8EAEC',
    textColor: '#1E2F41',
    bold: true,
  },
  'Đang không hoạt động': {
    backgroundColor: '#E8EAEC',
    textColor: '#1E2F41',
    bold: true,
  },
  'Đã xóa': { backgroundColor: '#FDECEB', textColor: '#EE4036', bold: true },
}

export function buildColumns(
  options: BuildColumnsOptions,
): CombinedColumnDef<Robot>[] {
  const {
    pageIndex,
    pageSize,
    loadingRobotId,
    showUnitColumn,
    canView = true,
    canUpdate = true,
    onViewDetail,
    onUpdate,
    onSetupLocation,
    onDelete,
    onToggleStatus,
  } = options

  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
      align: 'center',
    },
    {
      accessorKey: 'name',
      header: 'TÊN ROBOT',
      size: 150,
      cell: ({ row }) =>
        canView ? (
          <button
            type="button"
            className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
            onClick={() => onViewDetail?.(row.original)}
          >
            {row.original.name}
          </button>
        ) : (
          <span className="text-[#CE7A58]">{row.original.name}</span>
        ),
      align: 'left',
    },
    ...(showUnitColumn
      ? [
          {
            id: 'unitResponse',
            accessorKey: 'unitResponse.name',
            header: 'ĐƠN VỊ',
            size: 120,
            cell: ({ row }: CellContext<Robot, unknown>) =>
              row.original.unitResponse?.name || '',
            align: 'left',
          } satisfies CombinedColumnDef<Robot>,
        ]
      : []),
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 100,
      cell: ({ getValue }) => formatDateTime(getValue()),
      align: 'center',
    },
    {
      accessorKey: 'state',
      header: 'HIỆN TRẠNG',
      size: 100,
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${getStatusBadgeClass(row.original.state)}`}
        >
          {row.original.state}
        </span>
      ),
      align: 'center',
      exportStyle: { align: 'center' },
      exportStatusStyles: STATUS_EXCEL_STYLES,
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 80,
      cell: ({ row }) => {
        const r = row.original
        const isLoading = loadingRobotId === r.id
        return (
          <Spin spinning={isLoading} size="small">
            <Switch
              checked={r.status === 1}
              onCheckedChange={(checked) =>
                canUpdate && onToggleStatus?.({ ...r, status: checked ? 1 : 0 })
              }
              disabled={isLoading || !canUpdate || r.state === 'Đã xóa'}
            />
          </Spin>
        )
      },
      align: 'center',
    },
    {
      id: 'actions',
      size: 60,
      exportable: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <RobotActionsCell
            robot={row.original}
            onViewDetail={onViewDetail}
            onUpdate={onUpdate}
            onSetupLocation={onSetupLocation}
            onDelete={onDelete}
          />
        </div>
      ),
    },
  ]
}

function RobotActionsCell({
  robot,
  onViewDetail,
  onUpdate,
  onSetupLocation,
  onDelete,
}: RobotActionsCellProps) {
  const canView = usePermission('robot-view')
  const canUpdate = usePermission('robot-update')
  const canConfig = usePermission('robot-config')
  const canDelete = usePermission('robot-delete')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {canView && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onViewDetail?.(robot)}
          >
            Xem chi tiết
          </DropdownMenuItem>
        )}
        {canUpdate && robot.state !== 'Đã xóa' && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onUpdate?.(robot)}
          >
            Cập nhật
          </DropdownMenuItem>
        )}
        {canConfig && robot.state !== 'Đã xóa' && (
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => onSetupLocation?.(robot)}
          >
            Thiết lập cấu hình
          </DropdownMenuItem>
        )}
        {canDelete && robot.state !== 'Đã xóa' && (
          <DropdownMenuItem
            className="cursor-pointer text-red-600"
            onClick={() => onDelete?.(robot)}
          >
            Xóa
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
